import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const Section = styled.section`
  width: 100%;
  min-height: 100vh;
  background: #000;
  color: #fff;
  padding: 120px 20px 80px;
  display: flex;
  justify-content: center;
`;

const Container = styled.div`
  max-width: 800px;
  width: 100%;
  font-family: system-ui, -apple-system, sans-serif;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 2rem;
  text-align: center;
  background: linear-gradient(120deg, #fff, #9ca3af);
  background-size: 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientMove 8s ease-in-out infinite alternate;

  @keyframes gradientMove {
    0% { background-position: 0%; }
    100% { background-position: 100%; }
  }
`;

const Subtitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 2.5rem 0 1rem;
  color: #e5e5e5;
`;

const Text = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  opacity: 0.85;
  margin-bottom: 1.2rem;
  color: #ccc;
`;

const List = styled.ul`
  padding-left: 1.5rem;
  margin: 1rem 0;
  opacity: 0.9;
`;

const ListItem = styled.li`
  margin-bottom: 0.6rem;
  font-size: 1.05rem;
  line-height: 1.6;
`;

const Highlight = styled.strong`
  color: #fff;
`;

const ContactLink = styled.a`
  color: #60a5fa;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const TermsOfService = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Section>
        <Container>
          <Title>Terms of Service</Title>
          <Text>Last updated: November 15, 2025</Text>

          <Text>
            Welcome to <Highlight>Plano AI</Highlight>. By using our mobile application ("App"), 
            you agree to these Terms of Service ("Terms"). If you do not agree, please do not use the App.
          </Text>

          <Subtitle>1. Use of the App</Subtitle>
          <Text>You must be at least 13 years old to use Plano AI. You are responsible for:</Text>
          <List>
            <ListItem>Providing accurate information during signup</ListItem>
            <ListItem>Maintaining the security of your account</ListItem>
            <ListItem>All activity under your account</ListItem>
          </List>

          <Subtitle>2. License</Subtitle>
          <Text>
            We grant you a limited, non-transferable license to use the App for personal, non-commercial purposes. 
            You may not:
          </Text>
          <List>
            <ListItem>Reverse engineer, decompile, or modify the App</ListItem>
            <ListItem>Use the App for illegal purposes</ListItem>
            <ListItem>Interfere with App functionality</ListItem>
          </List>

          <Subtitle>3. Subscriptions & Payments</Subtitle>
          <Text>
            Plano AI offers free and paid plans. Paid subscriptions are billed monthly or annually via 
            Apple App Store or Google Play. You agree to:
          </Text>
          <List>
            <ListItem>Pay all charges at the prices in effect</ListItem>
            <ListItem>Authorize recurring payments</ListItem>
            <ListItem>Accept that refunds are subject to store policies</ListItem>
          </List>

          <Subtitle>4. AI-Generated Content</Subtitle>
          <Text>
            AI suggestions are for informational purposes only. Plano AI is not liable for:
          </Text>
          <List>
            <ListItem>Accuracy of AI recommendations</ListItem>
            <ListItem>Outcomes from following AI advice</ListItem>
            <ListItem>Any decisions you make based on the App</ListItem>
          </List>

          <Subtitle>5. Intellectual Property</Subtitle>
          <Text>
            All content, logos, and code in the App are owned by <Highlight>Plano AI Inc.</Highlight> 
            You may not copy, distribute, or create derivative works without written permission.
          </Text>

          <Subtitle>6. Termination</Subtitle>
          <Text>
            We may suspend or terminate your access at any time for violations of these Terms. 
            Upon termination, you must cease using the App.
          </Text>

          <Subtitle>7. Limitation of Liability</Subtitle>
          <Text>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, PLANO AI IS NOT LIABLE FOR INDIRECT, INCIDENTAL, 
            OR CONSEQUENTIAL DAMAGES. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID 
            IN THE PAST 12 MONTHS.
          </Text>

          <Subtitle>8. Governing Law</Subtitle>
          <Text>
            These Terms are governed by the laws of the State of California, USA. 
            Disputes will be resolved in San Francisco courts.
          </Text>

          <Subtitle>9. Changes to Terms</Subtitle>
          <Text>
            We may update these Terms. Continued use of the App after changes constitutes acceptance. 
            We will notify you of material changes via email or in-app.
          </Text>

          <Subtitle>10. Contact Us</Subtitle>
          <Text>
            Questions about these Terms?<br />
            Email: <ContactLink href="mailto:fluencboy@gmail.com">fluencboy@gmail.com</ContactLink><br />
            Company: Plano AI Inc.<br />
            Address: 548 Market St, San Francisco, CA 94104, USA
          </Text>

          <Text style={{ marginTop: '3rem', fontSize: '0.9rem', opacity: 0.6, textAlign: 'center' }}>
            © 2025 Plano AI Inc. All rights reserved.
          </Text>
        </Container>
      </Section>
    </motion.div>
  );
};

export default TermsOfService;